import React, { useState } from 'react';
import { AxiosError } from 'axios';
import { useFormikContext } from 'formik';
import { toast } from 'react-toastify';
import FormItem from '../../../components/Form/FormItem';
import FieldSet from '../../../components/Form/FieldSet';
import Label from '../../../components/Form/Label';
import Input from '../../../components/Form/Input';
import Title from '../../../components/Title';
import { UserData } from '../../../types/userData';
import inputMask from '../../../util/mask';
import ConfirmMobileModal from '../components/modals/ConfirmMobileModal';
import environmentConfig from '../../../config/environment-configs';
import { api } from '../../../services/api';
import { LoadingSpinner } from '../../../components/Loading/styles';
import { InfoPhones, FormInput, ExclamationIcon } from './styles';
import {
  Actions,
  Categories,
  GetInterventionCategory,
} from '../../../types/gtm';
import { User } from '../../../types/user';
import { useAuth } from '../../../hooks/useAuth';

export interface Props {
  title?: string;
  isIntervention?: string;
  width?: string;
  onConfirm?: () => void;
  user?: User;
  onMobilePhoneConfirmResult?: () => void;
}

export type ModalState = {
  show: boolean;
  alreadyRequest?: boolean;
  globoId?: string;
  number?: string;
  error?: string;
  dddCellPhone?: string;
  cellPhone?: string;
  requestMobilePhone?: boolean;
};

export type ModalProps = {
  modal: ModalState;
  setModal: (m: ModalState) => void;
  close: (b?: boolean) => void;
  onConfirm?: () => void;
  onConfirmResult?: () => void;
  onSkip?: () => void;
  user?: User;
};

export type ConfirmMobileRequest = {
  cellPhone: string;
  dddCellPhone: string;
};

const PhoneForm = ({
  title,
  isIntervention,
  width,
  onConfirm,
  onMobilePhoneConfirmResult,
  user,
}: Props) => {
  const getRecaptchaToken = async (): Promise<string> => {
    return new Promise(resolve => {
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(environmentConfig.GOOGLE_RECAPTCHA_KEY, {
            action: 'envioDeTokenSMS',
          })
          .then((token: string) => {
            resolve(token);
          });
      });
    });
  };

  const { values, errors, handleChange, initialValues, setFieldValue } =
    useFormikContext<UserData>();

  const restoreOriginalCellPhone = () => {
    setFieldValue('phones.cellPhone', initialValues.phones?.cellPhone || '');
  };

  const [confirmModal, setConfirmModal] = useState<ModalState>({
    show: false,
  });

  const closeModal = () => {
    setConfirmModal({ show: false });
  };

  const [loading, setLoading] = useState<boolean>(false);
  const { getValidToken } = useAuth();

  const maskInitPhone = (phone: string) => {
    return inputMask.phone(phone);
  };

  const confirmMobileRequest = async () => {
    if (loading) {
      return;
    }
    setLoading(true);
    window.dataLayer.push({
      event: 'customEventUserInteraction',
      event_category: Categories.EditUserPersonalData,
      event_action: Actions.ClickContinue,
      event_label: GetInterventionCategory(user),
      bd_suser_code: user?.globoId,
    });
    const unmaskPhone = inputMask.unMask(values.phones?.cellPhone);

    try {
      const token = await getValidToken();

      const body = {
        cellPhone: unmaskPhone.substring(2, 16),
        dddCellPhone: unmaskPhone.substring(0, 2),
      };

      const recaptchaToken = await getRecaptchaToken();
      api
        .put('/user/mobile/request', body, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Recaptcha-Token': recaptchaToken,
          },
        })
        .then(() => {
          setLoading(false);
          window.dataLayer.push({
            event: 'customEventUserInteraction',
            event_category: GetInterventionCategory(user),
            event_action: Actions.Success,
            event_label: 'Codigo Enviado',
            bd_suser_code: user?.globoId,
          });
          setConfirmModal({
            show: true,
            number: maskInitPhone(unmaskPhone),
            dddCellPhone: body.dddCellPhone,
            cellPhone: body.cellPhone,
          });
        })
        .catch((error: AxiosError) => {
          window.dataLayer.push({
            event: 'customEventUserInteraction',
            event_category: GetInterventionCategory(user),
            event_action: Actions.Error,
            event_label: 'Erro ao Reenviar Codigo',
            bd_suser_code: user?.globoId,
          });
          switch (error.response?.status) {
            case 429:
              toast.error(
                'Por favor, aguarde 30 segundos antes de requisitar um novo código.',
              );
              break;
            default:
              toast.error(
                'Não foi possível enviar o código de confirmação. Tente novamente mais tarde.',
              );
          }
          setLoading(false);
        });
    } catch (error) {
      setLoading(false);
      toast.error('Erro de autenticação. Tente novamente.');
    }
  };

  const getMobilebuttonText = () => {
    if (
      (user?.phones && user?.phones.mobilePhoneConfirmed) ||
      user?.hideMobilePhoneIntervention
    ) {
      return 'Alterar número';
    }
    return 'Confirmar celular';
  };

  const handlePhonesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.currentTarget.maxLength = 15;
    handleChange(e);
  };
  return (
    <div>
      {confirmModal.show && (
        <ConfirmMobileModal
          modal={confirmModal}
          setModal={setConfirmModal}
          close={closeModal}
          onConfirm={onConfirm}
          onConfirmResult={onMobilePhoneConfirmResult}
          onSkip={restoreOriginalCellPhone}
          user={user}
        />
      )}
      {isIntervention === 'true' && !confirmModal.show && (
        <FormItem>
          {title && <Title>{title}</Title>}

          <FormInput>
            <Label htmlFor="cellPhone">Celular</Label>
            <Input
              id="cellPhone"
              value={maskInitPhone(values.phones?.cellPhone || '')}
              onChange={handlePhonesChange}
              name="phones.cellPhone"
              placeHolderText="Informe seu número de celular"
              aria-label="Telefone celular"
              errorMessage={
                errors.phones?.cellPhone ? errors.phones?.cellPhone : ''
              }
              withButtonAlternative
              onClick={() => confirmMobileRequest()}
              buttonText={
                loading ? <LoadingSpinner isButton /> : 'Confirmar celular'
              }
              width={width}
              buttonDisabled={
                !values.phones?.cellPhone ||
                (!!errors.phones?.cellPhone &&
                  errors.phones.cellPhone !==
                    'É necessário confirmar seu celular antes de salvar as alterações.')
              }
              buttonVariant="primary"
            />
          </FormInput>
        </FormItem>
      )}
      {isIntervention !== 'true' && (
        <FormItem>
          {title && <Title>{title}</Title>}
          <div>
            <Label htmlFor="cellPhone">Celular</Label>
            <Input
              id="cellPhone"
              value={maskInitPhone(values.phones?.cellPhone || '')}
              onChange={handlePhonesChange}
              name="phones.cellPhone"
              placeHolderText="Informe seu número de celular"
              aria-label="Telefone celular"
              errorMessage={
                errors.phones?.cellPhone ? errors.phones?.cellPhone : ''
              }
              withButton
              onClick={() => confirmMobileRequest()}
              buttonText={
                loading ? <LoadingSpinner isButton /> : getMobilebuttonText()
              }
              buttonDisabled={
                !values.phones?.cellPhone ||
                (!!errors.phones?.cellPhone &&
                  errors.phones.cellPhone !==
                    'É necessário confirmar seu celular antes de salvar as alterações.')
              }
              buttonVariant="primary"
            />
          </div>
          <InfoPhones>
            <ExclamationIcon>!</ExclamationIcon>
            <p>
              Seu celular ajuda a recuperar sua conta, caso esqueça sua senha ou
              e-mail.
            </p>
          </InfoPhones>
          <FieldSet>
            <Label htmlFor="phone">Residencial</Label>
            <Input
              id="phone"
              name="phones.phone"
              onChange={handlePhonesChange}
              value={maskInitPhone(values.phones?.phone || '')}
              placeHolderText="Informe seu número residencial"
              aria-label="Telefone residencial"
              errorMessage={errors.phones?.phone ? errors.phones?.phone : ''}
            />
          </FieldSet>
        </FormItem>
      )}
    </div>
  );
};

export default PhoneForm;
